import { prefersReducedMotion } from '@theme/utilities';

/**
 * Scroll reveal utility
 *
 * Adds a one-time fade-in on scroll using opacity, blur, and translate.
 * Elements are revealed once when they enter the viewport and stay visible after that.
 *
 * Basic usage:
 * <div class="scroll-reveal">Default reveal from bottom</div>
 *
 * Direction variants:
 * <div class="scroll-reveal" data-reveal="left">Reveal from left</div>
 * <div class="scroll-reveal" data-reveal="right">Reveal from right</div>
 * <div class="scroll-reveal" data-reveal="down">Reveal from top</div>
 *
 * Per-element tuning:
 * <div
 *   class="scroll-reveal"
 *   data-reveal="left"
 *   data-reveal-delay="120"
 *   data-reveal-duration="800"
 *   data-reveal-distance="32"
 *   data-reveal-blur="12"
 * >
 *   Custom reveal
 * </div>
 *
 * Supported data attributes:
 * - data-reveal: "left" | "right" | "down"
 * - data-reveal-delay: transition delay in milliseconds
 * - data-reveal-duration: transition duration in milliseconds
 * - data-reveal-distance: translate distance in pixels
 * - data-reveal-blur: starting blur in pixels
 *
 * Internal state attributes added by the script:
 * - data-scroll-reveal-init="true": element is being observed and kept hidden
 * - data-revealed="true": element has already been revealed
 */
(() => {
  const SELECTOR = '.scroll-reveal';
  const INIT_ATTRIBUTE = 'data-scroll-reveal-init';
  const REVEALED_ATTRIBUTE = 'data-revealed';
  const observerSupported = typeof IntersectionObserver !== 'undefined';
  const reducedMotion = prefersReducedMotion();

  document.documentElement.classList.add('js-scroll-reveal');

  /**
   * @param {ParentNode | Element} root
   * @returns {HTMLElement[]}
   */
  const getRevealElements = (root) => {
    const elements = [];

    if (root instanceof HTMLElement && root.matches(SELECTOR)) {
      elements.push(root);
    }

    if ('querySelectorAll' in root) {
      elements.push(...root.querySelectorAll(SELECTOR));
    }

    return /** @type {HTMLElement[]} */ (elements);
  };

  /**
   * @param {HTMLElement} element
   */
  const applyRevealVariables = (element) => {
    const { revealDelay, revealDuration, revealDistance, revealBlur } = element.dataset;

    if (revealDelay) element.style.setProperty('--scroll-reveal-delay', `${revealDelay}ms`);
    if (revealDuration) element.style.setProperty('--scroll-reveal-duration', `${revealDuration}ms`);
    if (revealDistance) element.style.setProperty('--scroll-reveal-distance', `${revealDistance}px`);
    if (revealBlur) element.style.setProperty('--scroll-reveal-blur', `${revealBlur}px`);
  };

  /**
   * @param {HTMLElement} element
   */
  const revealElement = (element) => {
    element.setAttribute(REVEALED_ATTRIBUTE, 'true');
    element.removeAttribute(INIT_ATTRIBUTE);

    if (observerSupported) observer.unobserve(element);
  };

  /**
   * Reveal immediately if the element is already near the viewport to avoid a hide-then-show flash.
   *
   * @param {HTMLElement} element
   * @returns {boolean}
   */
  const shouldRevealImmediately = (element) => {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    return rect.top <= viewportHeight * 0.92 && rect.bottom >= 0;
  };

  /**
   * @param {HTMLElement} element
   */
  const initializeElement = (element) => {
    if (element.getAttribute(REVEALED_ATTRIBUTE) === 'true' || element.hasAttribute(INIT_ATTRIBUTE)) {
      return;
    }

    applyRevealVariables(element);

    if (reducedMotion || !observerSupported || shouldRevealImmediately(element)) {
      revealElement(element);
      return;
    }

    element.setAttribute(INIT_ATTRIBUTE, 'true');
    observer.observe(element);
  };

  /**
   * @param {ParentNode | Element} [root]
   */
  const init = (root = document) => {
    getRevealElements(root).forEach(initializeElement);
  };

  const observer = observerSupported
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            if (!(entry.target instanceof HTMLElement)) return;

            revealElement(entry.target);
          });
        },
        {
          threshold: 0.15,
          rootMargin: '0px 0px -8% 0px',
        }
      )
    : null;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    init(event.target);
  });

  window.addEventListener('load', () => init());

  if (typeof MutationObserver !== 'undefined') {
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          init(node);
        });
      });
    });

    mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
